// src/utils/index.ts
import { ethers } from "ethers";
import { v4 as uuidv4 } from "uuid";
function parseUrlParams(searchParams) {
  const params = typeof searchParams === "string" ? new URLSearchParams(searchParams) : searchParams;
  return {
    signature: params.get("signature") || "",
    message: params.get("message") || "",
    gameStartTab: params.get("game_start_tab") || "",
    hyveToken: params.get("hyve-token") || "",
    platform: params.get("platform") || ""
  };
}
function validateSignature(signature, message) {
  try {
    const recoveredAddress = ethers.verifyMessage(message, signature);
    return !!recoveredAddress;
  } catch (error) {
    console.error("Signature validation error:", error);
    return false;
  }
}
function handleVerifyMessage(signature, message) {
  try {
    const metadata = JSON.parse(decodeURI(message));
    if (!metadata.expiration || metadata.expiration < Date.now()) {
      return false;
    }
    const userAddress = metadata.userId || metadata.address;
    if (!userAddress) {
      return false;
    }
    const byteMessage = new TextEncoder().encode(message);
    const addressThatSignedMessage = ethers.verifyMessage(
      byteMessage,
      signature
    );
    if (!addressThatSignedMessage) {
      return false;
    }
    if (addressThatSignedMessage.toLowerCase() !== userAddress.toLowerCase()) {
      return false;
    }
    return userAddress;
  } catch (error) {
    console.error("Error verifying message:", error);
    return false;
  }
}
function verifyHyveToken(hyveToken, maxAgeSec = 600) {
  try {
    const parts = hyveToken.split(".");
    if (parts.length !== 4) {
      console.error(
        "Invalid hyve-token format: expected 4 parts, got",
        parts.length
      );
      return false;
    }
    const [signature, address, randomBase64, timestampStr] = parts;
    if (!signature || !address || !randomBase64 || !timestampStr) {
      console.error("Missing hyve-token components");
      return false;
    }
    const message = `${address}.${randomBase64}.${timestampStr}`;
    const recoveredAddress = ethers.verifyMessage(message, signature);
    if (recoveredAddress.toLowerCase() !== address.toLowerCase()) {
      console.error("Hyve-token signature verification failed");
      return false;
    }
    const timestamp = parseInt(timestampStr, 10);
    if (!Number.isFinite(timestamp)) {
      console.error("Invalid hyve-token timestamp");
      return false;
    }
    const now = Math.floor(Date.now() / 1e3);
    if (now - timestamp > maxAgeSec) {
      console.error(
        `Hyve-token expired (age: ${now - timestamp}s, max: ${maxAgeSec}s)`
      );
      return false;
    }
    return address;
  } catch (error) {
    console.error("Hyve-token verification error:", error);
    return false;
  }
}
function verifyAuthentication(params, maxAgeSec = 600) {
  if (params.hyveToken) {
    const modernAddress = verifyHyveToken(params.hyveToken, maxAgeSec);
    if (modernAddress) {
      return {
        isValid: true,
        address: modernAddress,
        method: "modern"
      };
    } else {
      return {
        isValid: false,
        address: null,
        method: "modern",
        error: "Modern token verification failed"
      };
    }
  }
  if (params.signature && params.message) {
    const legacyAddress = handleVerifyMessage(params.signature, params.message);
    if (legacyAddress) {
      return {
        isValid: true,
        address: legacyAddress,
        method: "legacy"
      };
    } else {
      return {
        isValid: false,
        address: null,
        method: "legacy",
        error: "Legacy token verification failed"
      };
    }
  }
  return {
    isValid: false,
    address: null,
    method: "none",
    error: "No authentication tokens provided"
  };
}
function generateUUID() {
  return uuidv4();
}
function isDomainAllowed(allowedDomains, hostname) {
  console.log("Hostname", hostname);
  if (!allowedDomains) return true;
  const targetHostname = hostname || "";
  if (!targetHostname) return false;
  if (targetHostname === "localhost" || targetHostname.startsWith("localhost:")) {
    return true;
  }
  const domains = Array.isArray(allowedDomains) ? allowedDomains : [allowedDomains];
  return domains.some((domain) => {
    if (domain === targetHostname) return true;
    if (domain.startsWith("*.")) {
      const baseDomain = domain.substring(2);
      return targetHostname.endsWith(baseDomain) && targetHostname.length > baseDomain.length && targetHostname.charAt(targetHostname.length - baseDomain.length - 1) === ".";
    }
    return false;
  });
}

// src/core/client.ts
var HyveClient = class {
  telemetryConfig;
  telemetryApiUrl;
  sessionId;
  userId = null;
  /**
   * Creates a new HyveClient instance
   * @param config Optional telemetry configuration
   */
  constructor(config) {
    this.telemetryConfig = {
      isDev: true,
      // Default to dev environment
      ...config
    };
    this.telemetryApiUrl = this.telemetryConfig.isDev ? "https://product-api.dev.hyve.gg/api/v1/partners/analytics/events" : "https://product-api.prod.hyve.gg/api/v1/partners/analytics/events";
    this.sessionId = generateUUID();
    console.log("[Hyve SDK] Client initialized with sessionId:", this.sessionId);
    console.log("[Hyve SDK] Telemetry environment:", this.telemetryConfig.isDev ? "dev" : "prod");
  }
  /**
   * Authenticates a user from URL parameters
   * @param urlParams URL parameters or search string
   * @returns Promise resolving to boolean indicating success
   */
  async authenticateFromUrl(urlParams) {
    try {
      const params = urlParams ? parseUrlParams(urlParams) : parseUrlParams(window.location.search);
      const authResult = verifyAuthentication({
        hyveToken: params.hyveToken,
        signature: params.signature,
        message: params.message
      });
      if (authResult.isValid && authResult.address) {
        this.userId = authResult.address;
        console.log("[Hyve SDK] Authentication successful:", authResult.address);
        console.log("[Hyve SDK] Authentication method:", authResult.method);
        return true;
      } else {
        console.error("[Hyve SDK] Authentication failed:", authResult.error);
        this.userId = null;
        return false;
      }
    } catch (error) {
      console.error("[Hyve SDK] Authentication error:", error);
      this.userId = null;
      return false;
    }
  }
  /**
   * Sends a telemetry event
   * @param eventLocation Location where the event occurred
   * @param eventCategory Main category of the event
   * @param eventAction Primary action taken
   * @param eventSubCategory Optional sub-category
   * @param eventSubAction Optional sub-action
   * @param eventDetails Optional event details
   * @param additionalData Optional additional data
   * @returns Promise resolving to boolean indicating success
   */
  async sendTelemetry(eventLocation, eventCategory, eventAction, eventSubCategory, eventSubAction, eventDetails, additionalData) {
    if (!this.telemetryConfig.apiKey) {
      console.error("[Hyve Telemetry] API key not configured");
      return false;
    }
    if (!this.userId) {
      console.warn("[Hyve Telemetry] No user ID - sending anonymous telemetry");
    }
    try {
      let finalEventDetails = null;
      if (additionalData && Object.keys(additionalData).length > 0) {
        finalEventDetails = JSON.stringify(additionalData);
      } else if (eventDetails) {
        finalEventDetails = eventDetails;
      }
      const telemetryEvent = {
        session_id: this.sessionId,
        hyve_user_id: this.userId || "anonymous",
        event_location: eventLocation,
        event_category: eventCategory,
        event_action: eventAction,
        event_sub_category: eventSubCategory || null,
        event_sub_action: eventSubAction || null,
        event_details: finalEventDetails
      };
      console.log("[Hyve Telemetry] Sending event:", telemetryEvent);
      const response = await fetch(this.telemetryApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": this.telemetryConfig.apiKey
        },
        body: JSON.stringify(telemetryEvent)
      });
      if (response.ok) {
        console.log("[Hyve Telemetry] Event sent successfully:", response.status);
        return true;
      } else {
        const errorText = await response.text();
        console.error("[Hyve Telemetry] Failed to send event:", response.status, errorText);
        return false;
      }
    } catch (error) {
      console.error("[Hyve Telemetry] Error sending event:", error);
      return false;
    }
  }
  /**
   * Updates the telemetry configuration
   * @param config New telemetry configuration
   */
  updateTelemetryConfig(config) {
    this.telemetryConfig = {
      ...this.telemetryConfig,
      ...config
    };
    this.telemetryApiUrl = this.telemetryConfig.isDev ? "https://product-api.dev.hyve.gg/api/v1/partners/analytics/events" : "https://product-api.prod.hyve.gg/api/v1/partners/analytics/events";
    console.log("[Hyve SDK] Telemetry config updated");
  }
  /**
   * Gets the current user ID
   * @returns Current user ID or null if not authenticated
   */
  getUserId() {
    return this.userId;
  }
  /**
   * Gets the current session ID
   * @returns Current session ID
   */
  getSessionId() {
    return this.sessionId;
  }
  /**
   * Checks if user is authenticated
   * @returns Boolean indicating authentication status
   */
  isUserAuthenticated() {
    return this.userId !== null;
  }
  /**
   * Logs out the current user
   */
  logout() {
    this.userId = null;
    console.log("[Hyve SDK] User logged out");
  }
  /**
   * Resets the client state
   */
  reset() {
    this.logout();
    this.sessionId = generateUUID();
    console.log("[Hyve SDK] Client reset with new sessionId:", this.sessionId);
  }
};
export {
  HyveClient,
  generateUUID,
  handleVerifyMessage,
  isDomainAllowed,
  parseUrlParams,
  validateSignature,
  verifyAuthentication,
  verifyHyveToken
};
